import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Masonry from 'react-masonry-css';
import { Helmet } from 'react-helmet';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './Gallery.css';

const BREAKPOINT_COLS = {
  default: 4,
  1100: 3,
  700: 2,
  500: 1,
};

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function GalleryPage({ title, data, seoDescription }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  // Shuffle once when the gallery mounts; stable across re-renders
  const shuffledData = useMemo(() => shuffle(data), [data]);
  const [loadedMap, setLoadedMap] = useState({});

  const selectedImage = selectedIndex !== null ? shuffledData[selectedIndex] : null;

  const handleNext = useCallback(() => {
    if (selectedIndex < shuffledData.length - 1) setSelectedIndex((i) => i + 1);
  }, [selectedIndex, shuffledData.length]);

  const handlePrev = useCallback(() => {
    if (selectedIndex > 0) setSelectedIndex((i) => i - 1);
  }, [selectedIndex]);

  const handleClose = useCallback(() => setSelectedIndex(null), []);

  const markLoaded = useCallback((index) => {
    setLoadedMap((m) => ({ ...m, [index]: true }));
  }, []);

  useEffect(() => {
    if (selectedImage === null) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') handleNext();
      else if (e.key === 'ArrowLeft') handlePrev();
      else if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedImage, handleNext, handlePrev, handleClose]);

  return (
    <div className="content-area">
      <Helmet>
        <title>{title} — Erik Hoffman Photos</title>
        <meta
          name="description"
          content={seoDescription || `${title} photography by Erik Hoffman.`}
        />
      </Helmet>

      <div className="gallery-container">
        <h1 className="header-text">{title}</h1>

        <Masonry
          breakpointCols={BREAKPOINT_COLS}
          className="my-masonry-grid"
          columnClassName="my-masonry-grid_column"
        >
          {shuffledData.map((item, index) => (
            <div
              className={`grid-item ${loadedMap[index] ? 'loaded' : 'skeleton'}`}
              key={item.path}
              onClick={() => setSelectedIndex(index)}
            >
              <img
                src={process.env.PUBLIC_URL + item.path}
                alt={item.alt}
                loading={index < 8 ? 'eager' : 'lazy'}
                onLoad={() => markLoaded(index)}
              />
            </div>
          ))}
        </Masonry>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div className="overlay" onClick={handleClose}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button className="lightbox-close" onClick={handleClose} aria-label="Close">
              <i className="fas fa-times" />
            </button>

            {/* Image counter */}
            <div className="lightbox-counter">
              {selectedIndex + 1} / {shuffledData.length}
            </div>

            {/* Image */}
            <img
              src={process.env.PUBLIC_URL + selectedImage.path}
              alt={selectedImage.alt}
            />

            {/* Prev / Next — overlaid on the image sides */}
            {selectedIndex > 0 && (
              <button className="lightbox-prev" onClick={handlePrev} aria-label="Previous photo">
                <i className="fas fa-chevron-left" />
              </button>
            )}
            {selectedIndex < shuffledData.length - 1 && (
              <button className="lightbox-next" onClick={handleNext} aria-label="Next photo">
                <i className="fas fa-chevron-right" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default GalleryPage;
